import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { loadStyles } from "../../../utils/cssLoader";
import { formatRssDate } from "../../../utils/interactivesHelper";
import AdWidget from "../adWidgets/AdWidget";
import MatchTimeline from "../../football/match/MatchTimeline";
import ComponentAdRenderer from "../adWidgets/ComponentAdRenderer";

// ── Service icon SVGs ───────────────────────────────────────────────────────
const SERVICE_ICONS = {
  youtube: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="feed-slider__yt-icon">
      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
    </svg>
  ),
  twitter: (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  ),
  instagram: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
      <circle cx="12" cy="12" r="5" />
      <circle cx="17.5" cy="6.5" r="1.5" fill="currentColor" stroke="none" />
    </svg>
  ),
  rss: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 11a9 9 0 0 1 9 9" />
      <path d="M4 4a16 16 0 0 1 16 16" />
      <circle cx="5" cy="19" r="1" fill="currentColor" />
    </svg>
  ),
};

const SERVICE_LABELS = {
  youtube: "YouTube",
  twitter: "X (Twitter)",
  instagram: "Instagram",
  rss: "RSS",
};

const SERVICE_EMOJIS = {
  twitter: "\uD83D\uDC26",
  youtube: "\u25B6",
  instagram: "\uD83D\uDCF8",
  rss: "\uD83D\uDCF0",
};

// ── Helper: send command to YouTube iframe via postMessage ──────────────
const ytCommand = (iframeEl, func, args = []) => {
  if (!iframeEl?.contentWindow) return;
  iframeEl.contentWindow.postMessage(
    JSON.stringify({ event: "command", func, args }),
    "*",
  );
};

// ── Helper: build YouTube embed URL with API enabled ────────────────────
const buildEmbedUrl = (url, autoplay) => {
  if (!url) return "";
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}enablejsapi=1&autoplay=${autoplay ? 1 : 0}&mute=1`;
};

// ── Feed card ─────────────────────────────────────────────────────────────
const FeedSliderCard = ({ item = {}, isActive, isMobile, showCaption = true, onClose = null }) => {
  const {
    title, link, description, pubDate, image,
    video, embedUrl, socialEmbed, author, service,
  } = item;

  // Native video refs/state
  const videoElRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [paused, setPaused] = useState(false);

  // YouTube embed ref + state
  const embedRef = useRef(null);
  const [embedMuted, setEmbedMuted] = useState(true);
  const [embedPaused, setEmbedPaused] = useState(false);

  const setVideoRef = useCallback((el) => {
    videoElRef.current = el;
    if (!el) return;
    if (isActive) el.play().catch(() => {});
  }, [isActive]);

  // Native video: play/pause on active change
  useEffect(() => {
    const el = videoElRef.current;
    if (!el) return;
    if (isActive) { el.play().catch(() => {}); setPaused(false); }
    else el.pause();
  }, [isActive]);

  // YouTube embed: play/pause on active change
  useEffect(() => {
    const iframe = embedRef.current;
    if (!iframe) return;
    if (isActive) { ytCommand(iframe, "playVideo"); setEmbedPaused(false); }
    else ytCommand(iframe, "pauseVideo");
  }, [isActive]);

  // Native video controls
  const toggleMute = (e) => {
    e.stopPropagation();
    const el = videoElRef.current;
    if (!el) return;
    el.muted = !el.muted;
    setMuted(el.muted);
  };

  const togglePlay = (e) => {
    e.stopPropagation();
    const el = videoElRef.current;
    if (!el) return;
    if (el.paused) { el.play().catch(() => {}); setPaused(false); }
    else { el.pause(); setPaused(true); }
  };

  // YouTube embed controls
  const toggleEmbedPlay = (e) => {
    e.stopPropagation();
    const iframe = embedRef.current;
    if (!iframe) return;
    if (embedPaused) { ytCommand(iframe, "playVideo"); setEmbedPaused(false); }
    else { ytCommand(iframe, "pauseVideo"); setEmbedPaused(true); }
  };

  const toggleEmbedMute = (e) => {
    e.stopPropagation();
    const iframe = embedRef.current;
    if (!iframe) return;
    if (embedMuted) { ytCommand(iframe, "unMute"); setEmbedMuted(false); }
    else { ytCommand(iframe, "mute"); setEmbedMuted(true); }
  };

  const hasVideo = Boolean(video);
  const hasEmbed = Boolean(embedUrl);
  const hasSocialEmbed = Boolean(socialEmbed);
  const svc = service?.toLowerCase();
  const isInCompactSlider = Boolean(item._compact);

  const cardClass = [
    "feed-slider__card",
    hasSocialEmbed && "feed-slider__card--social",
    hasEmbed && "feed-slider__card--embed",
  ].filter(Boolean).join(" ");

  const mediaClass = [
    "feed-slider__media",
    hasSocialEmbed && "feed-slider__media--social",
  ].filter(Boolean).join(" ");

  return (
    <div className={cardClass}>
      {/* ── Media panel (left 2/3 on desktop) ── */}
      <div className={mediaClass}>
        {hasVideo ? (
          <>
            <video
              ref={setVideoRef}
              src={video}
              className="feed-slider__video"
              muted loop playsInline preload="auto"
              poster={image || undefined}
              onClick={togglePlay}
            />
            {!isMobile && (
              <>
                <button className="feed-slider__vid-btn feed-slider__vid-btn--mute" onClick={toggleMute} aria-label={muted ? "Unmute" : "Mute"}>
                  {muted ? "\uD83D\uDD07" : "\uD83D\uDD0A"}
                </button>
                <button className="feed-slider__vid-btn feed-slider__vid-btn--play" onClick={togglePlay} aria-label={paused ? "Play" : "Pause"}>
                  {paused ? "\u25B6" : "\u23F8"}
                </button>
              </>
            )}
          </>
        ) : hasEmbed ? (
          <>
            <iframe
              ref={embedRef}
              className="feed-slider__embed"
              src={isActive ? buildEmbedUrl(embedUrl, true) : ""}
              title={title || "Video"}
              style={{ border: "none" }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
            {/* Full-area tap overlay for play/pause */}
            <div className="feed-slider__embed-tap" onClick={toggleEmbedPlay} />
            {/* Play + Mute buttons — right center */}
            <div className="feed-slider__embed-bar">
              <button className="feed-slider__embed-btn" onClick={toggleEmbedPlay} aria-label={embedPaused ? "Play" : "Pause"}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  {embedPaused ? (
                    <polygon points="6 3 20 12 6 21 6 3" />
                  ) : (
                    <path d="M6 4h4v16H6zM14 4h4v16h-4z" />
                  )}
                </svg>
              </button>
              <button className="feed-slider__embed-btn" onClick={toggleEmbedMute} aria-label={embedMuted ? "Unmute" : "Mute"}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  {embedMuted ? (
                    <>
                      <path d="M11 4.702a.705.705 0 0 0-1.203-.498L6.413 7.587A1.4 1.4 0 0 1 5.416 8H3a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2.416a1.4 1.4 0 0 1 .997.413l3.383 3.384A.705.705 0 0 0 11 19.298z" />
                      <line x1="22" x2="16" y1="9" y2="15" />
                      <line x1="16" x2="22" y1="9" y2="15" />
                    </>
                  ) : (
                    <>
                      <path d="M11 4.702a.705.705 0 0 0-1.203-.498L6.413 7.587A1.4 1.4 0 0 1 5.416 8H3a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2.416a1.4 1.4 0 0 1 .997.413l3.383 3.384A.705.705 0 0 0 11 19.298z" />
                      <path d="M16 9a5 5 0 0 1 0 6" />
                      <path d="M19.364 18.364a9 9 0 0 0 0-12.728" />
                    </>
                  )}
                </svg>
              </button>
            </div>
          </>
        ) : hasSocialEmbed ? (
          <>
            <iframe
              className="feed-slider__social"
              src={isActive ? socialEmbed : ""}
              title={title || service || "Post"}
              style={isInCompactSlider ? { border: "none", pointerEvents: "none" } : { border: "none" }}
              scrolling="no"
              allow="autoplay; encrypted-media"
            />
            {/* Guard area over bottom-right so nav buttons can receive taps */}
            {isMobile && <div className="feed-slider__nav-guard" />}
          </>
        ) : image ? (
          <img
            src={image}
            alt={title || ""}
            className="feed-slider__image"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <div className={`feed-slider__no-media${svc === "instagram" ? " feed-slider__no-media--instagram" : ""}`}>
            {svc === "instagram" ? (
              <svg className="feed-slider__no-media-icon" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                <circle cx="12" cy="12" r="5" />
                <circle cx="17.5" cy="6.5" r="1.5" fill="white" stroke="none" />
              </svg>
            ) : (SERVICE_EMOJIS[svc] || "\uD83D\uDCF0")}
          </div>
        )}
      </div>

      {/* ── Gradient (mobile only via CSS) ── */}
      <div className="feed-slider__gradient" />

      {/* ── Mobile video actions (native video only — YouTube has its own controls) ── */}
      {isMobile && hasVideo && (
        <div className="feed-slider__actions">
          <button className="feed-slider__action-btn" onClick={toggleMute}>
            <span className="feed-slider__action-circle">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                {muted ? (
                  <>
                    <path d="M11 4.702a.705.705 0 0 0-1.203-.498L6.413 7.587A1.4 1.4 0 0 1 5.416 8H3a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2.416a1.4 1.4 0 0 1 .997.413l3.383 3.384A.705.705 0 0 0 11 19.298z" />
                    <line x1="22" x2="16" y1="9" y2="15" />
                    <line x1="16" x2="22" y1="9" y2="15" />
                  </>
                ) : (
                  <>
                    <path d="M11 4.702a.705.705 0 0 0-1.203-.498L6.413 7.587A1.4 1.4 0 0 1 5.416 8H3a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2.416a1.4 1.4 0 0 1 .997.413l3.383 3.384A.705.705 0 0 0 11 19.298z" />
                    <path d="M16 9a5 5 0 0 1 0 6" />
                    <path d="M19.364 18.364a9 9 0 0 0 0-12.728" />
                  </>
                )}
              </svg>
            </span>
            <span className="feed-slider__action-label">{muted ? "Unmute" : "Mute"}</span>
          </button>
        </div>
      )}

      {/* ── Social card footer (mobile only — hidden on desktop via CSS) ── */}
      {showCaption && hasSocialEmbed && (
        <div className="feed-slider__footer">
          <div className="feed-slider__source">
            {SERVICE_ICONS[svc] && (
              <span className="feed-slider__source-icon">{SERVICE_ICONS[svc]}</span>
            )}
            <span className="feed-slider__source-text">
              {author || SERVICE_LABELS[svc] || service || ""}
            </span>
          </div>
          {title && <h2 className="feed-slider__title">{title}</h2>}
          {link && (
            <a href={link} target="_blank" rel="noopener noreferrer" className="feed-slider__view-btn" onClick={(e) => e.stopPropagation()}>
              View Original
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 3h6v6" />
                <path d="M10 14 21 3" />
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
              </svg>
            </a>
          )}
        </div>
      )}

      {/* ── Body sidebar (right 1/3 on desktop) — hidden for social cards ── */}
      {showCaption && !hasSocialEmbed && (
        <div className="feed-slider__body">
          {/* Header: service icon + close */}
          <div className="feed-slider__body-header">
            <div className="feed-slider__source">
              {SERVICE_ICONS[svc] && (
                <span className="feed-slider__source-icon">{SERVICE_ICONS[svc]}</span>
              )}
              <span className="feed-slider__source-text">
                {SERVICE_LABELS[svc] || service || ""}
              </span>
            </div>
            {onClose && (
              <button className="feed-slider__close" onClick={() => onClose()} aria-label="Close">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6 6 18" />
                  <path d="m6 6 12 12" />
                </svg>
              </button>
            )}
          </div>

          {title && <h2 className="feed-slider__title">{title}</h2>}
          {description && <p className="feed-slider__desc">{description}</p>}

          {/* Spacer pushes footer to bottom */}
          <div className="feed-slider__body-spacer" />

          {/* Footer: author, date, view-original */}
          <div className="feed-slider__body-footer">
            {author && <span className="feed-slider__author">{author}</span>}
            {pubDate && <span className="feed-slider__date">{formatRssDate(pubDate)}</span>}
            {link && (
              <a href={link} target="_blank" rel="noopener noreferrer" className="feed-slider__view-btn" onClick={(e) => e.stopPropagation()}>
                View Original
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M15 3h6v6" />
                  <path d="M10 14 21 3" />
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                </svg>
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Ad card ─────────────────────────────────────────────────────────────────
const AdSlide = ({ adConfig, compIndex = 0 }) => {
  const renderAd = () => {
    if (adConfig?.type === "components") {
      const comps = adConfig.components || [];
      const comp = comps[compIndex % comps.length];
      return (
        <ComponentAdRenderer
          name={comp.name}
          componentData={comp.componentData}
          difference={adConfig.difference || "+0"}
          socketConfig={adConfig.socketConfig}
        />
      );
    }
    if (adConfig?.type === "timeline") {
      return (
        <MatchTimeline
          matchId={adConfig.matchId}
          baseUrl={adConfig.baseUrl}
          sport={adConfig.sport || "football"}
          loadCSS={false}
        />
      );
    }
    return (
      <AdWidget
        ratio="300x600"
        src={adConfig?.src || ""}
        link={adConfig?.link || ""}
        alt={adConfig?.alt || "Advertisement"}
        loadCSS={false}
      />
    );
  };

  return (
    <div className="feed-slider__card feed-slider__card--ad">{renderAd()}</div>
  );
};

// ── Build slide sequence ────────────────────────────────────────────────────
const buildSlides = (feedItems, adConfig, adEvery = 3) => {
  const slides = [];
  let compCycleIdx = 0;
  feedItems.forEach((item, i) => {
    slides.push({ type: "feed", item });
    if (adConfig && (i + 1) % adEvery === 0) {
      const compIndex = adConfig.type === "components" ? compCycleIdx++ : null;
      slides.push({ type: "ad", compIndex });
    }
  });
  return slides;
};

// ── Detect mobile ───────────────────────────────────────────────────────────
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= 640,
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 640px)");
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return isMobile;
};

// ── Mobile bottom nav bar ───────────────────────────────────────────────────
const MobileBottomNav = ({ current, total, onPrev, onNext }) => {
  return createPortal(
    <div className="fs-mobile-nav">
      <button
        className="fs-mobile-nav__btn"
        disabled={current === 0}
        onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); onPrev(); }}
        aria-label="Previous"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="m18 15-6-6-6 6" />
        </svg>
      </button>
      <button
        className="fs-mobile-nav__btn"
        disabled={current >= total - 1}
        onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); onNext(); }}
        aria-label="Next"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
    </div>,
    document.body,
  );
};

// ── Main slider overlay ─────────────────────────────────────────────────────
const FeedSlider = ({
  items = [],
  startIndex = 0,
  onClose,
  adConfig = null,
  adEvery = 3,
  onNeedMore = null,
  hasMore = false,
  ratio = "",
  showCaption = true,
}) => {
  const slides = buildSlides(items, adConfig, adEvery);
  const isMobile = useIsMobile();

  // Parse ratio string (e.g. "300x250") into width/height
  const [ratioW, ratioH] = ratio ? ratio.split("x").map(Number) : [0, 0];
  const isCompact = ratioW > 0 && ratioH > 0;

  const initialSlide = (() => {
    let feedCount = -1;
    for (let i = 0; i < slides.length; i++) {
      if (slides[i].type === "feed") feedCount++;
      if (feedCount === startIndex) return i;
    }
    return 0;
  })();

  const [current, setCurrent] = useState(initialSlide);
  const [showHint, setShowHint] = useState(true);
  const frameRef = useRef(null);

  useEffect(() => { loadStyles("interactives"); }, []);

  // Lock body scroll (only when used as overlay)
  useEffect(() => {
    if (!onClose) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev || ""; };
  }, [onClose]);

  // Hide hint
  useEffect(() => {
    const t = setTimeout(() => setShowHint(false), 2500);
    return () => clearTimeout(t);
  }, []);

  const goNext = useCallback(() => {
    setCurrent((c) => (c < slides.length - 1 ? c + 1 : c));
  }, [slides.length]);

  const goPrev = useCallback(() => {
    setCurrent((c) => (c > 0 ? c - 1 : c));
  }, []);

  // Fetch next page
  useEffect(() => {
    if (!onNeedMore || !hasMore) return;
    if (current >= slides.length - 3) onNeedMore();
  }, [current, slides.length, onNeedMore, hasMore]);

  // Keyboard
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose?.();
      if (isCompact) {
        if (e.key === "ArrowDown" || e.key === "ArrowRight") goNext();
        if (e.key === "ArrowUp" || e.key === "ArrowLeft") goPrev();
      } else {
        if (e.key === "ArrowRight" || e.key === "ArrowDown") goNext();
        if (e.key === "ArrowLeft" || e.key === "ArrowUp") goPrev();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [goNext, goPrev, onClose, isCompact]);

  if (!slides.length) return null;

  const slide = slides[current];
  const isAd = slide.type === "ad";
  const feedPosition = slides.slice(0, current + 1).filter((s) => s.type === "feed").length;
  const totalFeed = items.length;

  // ── Compact slider (ratio-based, mobile-style) ──────────────────────────
  if (isCompact) {
    const stripTransform = `translateY(${-current * 100}%)`;
    const inline = !onClose;

    const outerStyle = inline
      ? { position: "relative", inset: "auto", zIndex: "auto", background: "none", backdropFilter: "none", alignItems: "flex-start", justifyContent: "flex-start" }
      : undefined;
    const frameStyle = inline
      ? { width: ratioW, height: ratioH, maxWidth: "100%", boxShadow: "none", border: "none" }
      : { width: ratioW, height: ratioH };

    return (
      <>
        <div className="feed-slider feed-slider--compact" style={outerStyle}>
          {!inline && <div className="feed-slider__backdrop" onClick={() => onClose?.()} />}

          <div
            className="feed-slider__frame"
            ref={frameRef}
            style={frameStyle}
          >
            {/* ── Top bar ── */}
            <div className="feed-slider__top-bar" style={inline ? { justifyContent: "flex-end" } : undefined}>
              {!inline && (
                <button className="feed-slider__close" onClick={() => onClose?.()} aria-label="Close">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 6 6 18" />
                    <path d="m6 6 12 12" />
                  </svg>
                </button>
              )}
              <div className="feed-slider__pills">
                <span className="feed-slider__counter">
                  {isAd ? "Ad" : `${feedPosition} / ${totalFeed}`}
                </span>
              </div>
            </div>

            {/* ── Strip (vertical) ── */}
            <div className="feed-slider__strip" style={{ transform: stripTransform }}>
              {slides.map((s, i) =>
                s.type === "ad" ? (
                  <AdSlide key={`ad-${i}`} adConfig={adConfig} compIndex={s.compIndex ?? 0} />
                ) : (
                  <FeedSliderCard key={i} item={{ ...s.item, _compact: true }} isActive={i === current} isMobile={true} showCaption={showCaption} />
                ),
              )}
            </div>

            {showHint && current < slides.length - 1 && (
              <div className="feed-slider__hint">{"\u2191"} swipe up for next</div>
            )}

            {current === slides.length - 1 && hasMore && (
              <div className="feed-slider__loading-more">Loading&hellip;</div>
            )}

            {/* ── Nav arrows (up/down, inside frame) ── */}
            {current > 0 && (
              <button className="feed-slider__nav feed-slider__nav--prev" onClick={(e) => { e.stopPropagation(); e.preventDefault(); }} onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); goPrev(); }} aria-label="Previous">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m18 15-6-6-6 6" />
                </svg>
              </button>
            )}
            {current < slides.length - 1 && (
              <button className="feed-slider__nav feed-slider__nav--next" onClick={(e) => { e.stopPropagation(); e.preventDefault(); }} onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); goNext(); }} aria-label="Next">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </>
    );
  }

  // ── Default large dialog slider ──────────────────────────────────────────
  const stripTransform = isMobile
    ? `translateY(${-current * 100}%)`
    : `translateX(${-current * 100}%)`;

  return (
    <>
      <div className="feed-slider">
        <div className="feed-slider__backdrop" onClick={() => onClose?.()} />

        <div className="feed-slider__frame" ref={frameRef}>
          {/* ── Top bar (counter + overlay close for social/ad cards) ── */}
          <div className="feed-slider__top-bar">
            <div className="feed-slider__pills">
              <span className="feed-slider__counter">
                {isAd ? "Ad" : `${feedPosition} / ${totalFeed}`}
              </span>
            </div>
            <button className="feed-slider__close feed-slider__close--overlay" onClick={() => onClose?.()} aria-label="Close">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
          </div>

          {/* ── Strip ── */}
          <div className="feed-slider__strip" style={{ transform: stripTransform }}>
            {slides.map((s, i) =>
              s.type === "ad" ? (
                <AdSlide key={`ad-${i}`} adConfig={adConfig} compIndex={s.compIndex ?? 0} />
              ) : (
                <FeedSliderCard key={i} item={s.item} isActive={i === current} isMobile={isMobile} onClose={onClose} />
              ),
            )}
          </div>

          {showHint && current < slides.length - 1 && (
            <div className="feed-slider__hint">
              {isMobile ? "\u2191 swipe up for next" : "\u2190 swipe for more \u2192"}
            </div>
          )}

          {current === slides.length - 1 && hasMore && (
            <div className="feed-slider__loading-more">Loading&hellip;</div>
          )}
        </div>

        {/* ── Desktop nav arrows (outside __frame, siblings of it) ── */}
        {current > 0 && (
          <button className="feed-slider__nav feed-slider__nav--prev" onClick={goPrev} aria-label="Previous">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m15 18-6-6 6-6" />
            </svg>
          </button>
        )}
        {current < slides.length - 1 && (
          <button className="feed-slider__nav feed-slider__nav--next" onClick={goNext} aria-label="Next">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m9 18 6-6-6-6" />
            </svg>
          </button>
        )}
      </div>

      {/* Mobile nav: rendered via portal on document.body */}
      {isMobile && (
        <MobileBottomNav
          current={current}
          total={slides.length}
          onPrev={goPrev}
          onNext={goNext}
        />
      )}
    </>
  );
};

export default FeedSlider;
